import type { ISpeakerProfile, ISpeakerSuggestion } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { ActionIcon, Badge, Tooltip } from "@mantine/core"
import {
  IconCheck,
  IconMicrophone,
  IconPencil,
  IconPlayerPlayFilled,
  IconSparkles,
  IconUserCheck,
  IconX,
} from "@tabler/icons-react"
import clsx from "clsx"
import { type KeyboardEvent, type MouseEvent, useCallback, useEffect, useRef, useState } from "react"
import { identityInkColor, identityTextColor } from "../../lib/speaker-colors"
import { summariseSpeakerEvidence } from "../../lib/speaker-evidence"
import { type ISpeakerVM, speakerDisplayLabel } from "../../lib/speaker-vm"
import { fmtTimestamp } from "../MeetingSummary/helpers"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

/** How long the "just confirmed" success ring + tag lingers before settling. */
const FRESH_MS = 2500

/** The resolved display label plus a localized `(remote)` suffix for speakers who
 *  joined remotely. The label unifies an unidentified slot's number across the
 *  room + far-end channels ("Speaker 3"); the suffix now rides physical
 *  {@link ISpeakerVM.presence}, not the capture channel — `in_room`/`unknown`
 *  (mono / legacy meetings) render no suffix. DISPLAY-ONLY — every edit/rename key
 *  keeps the raw `displayName`/`label`, which is what the roster and dialogue store. */
function SpeakerName({ speaker }: { speaker: ISpeakerVM }): React.JSX.Element {
  const { i18n } = useLingui()
  return (
    <>
      {speakerDisplayLabel(speaker, i18n)}
      {speaker.presence === "remote" ? (
        // Spacing lives on .farEnd (margin-left), NOT a {" "} text node — the
        // roster variant renders inside Mantine Button's flex label, where
        // whitespace text nodes collapse to zero width ("Bob(remote)").
        <span className={classes.farEnd}>
          <Trans>(remote)</Trans>
        </span>
      ) : null}
    </>
  )
}

/** Leading recognition-source icon for the roster variant (voiceprint / manual / llm). */
function RecognitionIcon({ source }: { source: ISpeakerProfile["recognition_source"] }): React.JSX.Element | null {
  const { i18n } = useLingui()
  if (source === "voiceprint") {
    return (
      <Tooltip label={i18n._(msg`Recognised by voiceprint`)} withArrow>
        <IconMicrophone size={12} className={classes.recognitionIcon} />
      </Tooltip>
    )
  }
  if (source === "manual") {
    return (
      <Tooltip label={i18n._(msg`Manually confirmed`)} withArrow>
        <IconUserCheck size={12} className={classes.recognitionIcon} />
      </Tooltip>
    )
  }
  if (source === "llm") {
    return (
      <Tooltip label={i18n._(msg`AI-identified from the conversation`)} withArrow>
        <IconSparkles size={12} className={classes.recognitionIcon} />
      </Tooltip>
    )
  }
  return null
}

/**
 * The `{name}?` proposal badge (evidence tooltip) plus confirm / reject icons for
 * a speaker whose status is "suggested". Rendered as a sibling of the chip — never
 * a child of the chip's `<button>` — so its own buttons don't nest inside it.
 */
function SuggestionAffordance({
  suggestion,
  editKey,
  resolvedColor,
  pending,
  readOnly = false,
  onConfirm,
  onReject,
}: {
  suggestion: ISpeakerSuggestion
  editKey: string
  resolvedColor: string
  pending?: Set<string>
  /** Render the `{name}?` badge without the confirm / reject controls. */
  readOnly?: boolean
  onConfirm?: (label: string, name: string) => void
  onReject?: (label: string, name: string) => void
}): React.JSX.Element {
  const { i18n } = useLingui()
  const evidence = summariseSpeakerEvidence(suggestion.sources, i18n)
  const confirmKey = `${editKey}|${suggestion.name}|confirm`
  const rejectKey = `${editKey}|${suggestion.name}|reject`
  // Guard against a host that wraps the whole chip in a clickable row: the ✓/✗
  // must not bubble up into the chip's own onEdit.
  const stop = (event: MouseEvent): void => event.stopPropagation()

  const badge = (
    <Tooltip label={evidence} disabled={!evidence} withArrow>
      <Badge
        size="sm"
        variant="light"
        color={resolvedColor}
        styles={{ label: { color: identityTextColor(resolvedColor) } }}
      >
        {`${suggestion.name}?`}
      </Badge>
    </Tooltip>
  )

  // Display-only: the recognition badge with no confirm/reject affordance.
  if (readOnly) return <span className={classes.suggestion}>{badge}</span>

  return (
    <span className={classes.suggestion}>
      {badge}
      <ActionIcon
        size="xs"
        variant="subtle"
        color="success"
        loading={pending?.has(confirmKey) ?? false}
        onClick={(event) => {
          stop(event)
          onConfirm?.(editKey, suggestion.name)
        }}
        aria-label={i18n._(msg`Confirm ${suggestion.name}`)}
        title={i18n._(msg`Confirm ${suggestion.name}`)}
      >
        <IconCheck size={12} />
      </ActionIcon>
      <ActionIcon
        size="xs"
        variant="subtle"
        color="danger"
        loading={pending?.has(rejectKey) ?? false}
        onClick={(event) => {
          stop(event)
          onReject?.(editKey, suggestion.name)
        }}
        aria-label={i18n._(msg`Reject ${suggestion.name}`)}
        title={i18n._(msg`Reject ${suggestion.name}`)}
      >
        <IconX size={12} />
      </ActionIcon>
    </span>
  )
}

/**
 * An in-card action rendered as a `role="button"` span, never a nested `<button>`
 * — the profile card is itself a `<button>`, so its inner Yes / No / confirm /
 * listen controls must not be real buttons. Activates on click + Enter/Space and
 * stops propagation so the card's own edit handler doesn't also fire.
 */
function ActionChip({
  tone,
  ariaLabel,
  onActivate,
  children,
}: {
  tone: "yes" | "no" | "listen"
  ariaLabel: string
  onActivate: () => void
  children: React.ReactNode
}): React.JSX.Element {
  const activate = (event: MouseEvent | KeyboardEvent): void => {
    event.stopPropagation()
    onActivate()
  }
  const className =
    tone === "listen" ? classes.listenDot : clsx(classes.action, tone === "yes" ? classes.actionYes : classes.actionNo)
  return (
    // A real <button> would nest inside the card's own <button> (invalid) — the
    // card is the primary target, these are secondary role=button spans.
    // biome-ignore lint/a11y/useSemanticElements: role=button span avoids nesting a <button> in the card's <button>
    <span
      role="button"
      tabIndex={0}
      className={className}
      aria-label={ariaLabel}
      onClick={activate}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          activate(event)
        }
      }}
    >
      {children}
    </span>
  )
}

/** The identity avatar for a profile card: identity fill + ink initials, or a
 *  dashed neutral "?" placeholder while the speaker is still unidentified. */
function CardAvatar({ speaker }: { speaker: ISpeakerVM }): React.JSX.Element {
  if (speaker.status === "unidentified") {
    return (
      <span className={classes.cardAvatar} data-unidentified="true" aria-hidden>
        ?
      </span>
    )
  }
  return (
    <span
      className={classes.cardAvatar}
      // Identity hue rides inline (allowed for graphics); ink picks black/white
      // for WCAG contrast against that hue.
      style={{ background: speaker.resolvedColor, color: identityInkColor(speaker.resolvedColor) }}
      aria-hidden
    >
      {speaker.initials}
    </span>
  )
}

/**
 * The `card` + `compact` variants — the profile-card restyle. A two-line card
 * (avatar + name + a state-driven working subline) with a compact single-line
 * pill density. The whole card opens the speaker editor; the inner Yes / No /
 * confirm / listen controls intercept. Owns the transient "fresh" success state
 * that pops in when a confirm this card initiated resolves.
 */
function SpeakerProfileCard({
  speaker,
  variant,
  onEdit,
  onConfirmSuggestion,
  onRejectSuggestion,
  onListen,
  suggestionPending,
  "data-testid": dataTestid,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()

  // ── Transient "fresh" success state ──────────────────────────────────────
  // When a confirm THIS card started (via `beginConfirm`) round-trips — its
  // pending key appears then clears — flash a success ring + "saved" tag for a
  // beat, then settle. Self-contained: no host state, driven only by watching
  // our own key leave `suggestionPending`.
  const [fresh, setFresh] = useState(false)
  const awaitingRef = useRef<string | null>(null)
  const sawPendingRef = useRef(false)
  const freshTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const editKey = speaker.label ?? speaker.displayName

  const beginConfirm = useCallback(
    (confirmName: string): void => {
      awaitingRef.current = `${editKey}|${confirmName}|confirm`
      sawPendingRef.current = false
      onConfirmSuggestion?.(editKey, confirmName)
    },
    [editKey, onConfirmSuggestion],
  )

  useEffect(() => {
    const key = awaitingRef.current
    if (!key) return
    if (suggestionPending?.has(key)) {
      sawPendingRef.current = true
      return
    }
    // Key cleared — but only celebrate if we actually saw it pending first (so a
    // never-pending host, or the initial render, doesn't false-trigger).
    if (sawPendingRef.current) {
      awaitingRef.current = null
      sawPendingRef.current = false
      setFresh(true)
      if (freshTimer.current) clearTimeout(freshTimer.current)
      freshTimer.current = setTimeout(() => setFresh(false), FRESH_MS)
    }
  }, [suggestionPending])

  useEffect(
    () => () => {
      if (freshTimer.current) clearTimeout(freshTimer.current)
    },
    [],
  )

  // ── Derived state ─────────────────────────────────────────────────────────
  const name = speaker.displayName
  const testId = dataTestid ?? `speaker-chip-${editKey}`
  const suggestion = speaker.suggestion
  const isSuggested = speaker.status === "suggested" && Boolean(suggestion)
  const isAiNamed = speaker.status === "named" && speaker.recognitionSource === "llm"
  const isUnidentified = speaker.status === "unidentified"
  const pendingName = isSuggested && suggestion ? suggestion.name : isAiNamed ? name : null
  const isPending =
    pendingName != null &&
    ((suggestionPending?.has(`${editKey}|${pendingName}|confirm`) ?? false) ||
      (suggestionPending?.has(`${editKey}|${pendingName}|reject`) ?? false))

  const interactive = Boolean(onEdit)
  const editLabel = speaker.needsAttention ? i18n._(msg`Identify ${name}`) : i18n._(msg`Edit profile ${name}`)
  const dataState = fresh ? "fresh" : isSuggested ? "suggested" : isUnidentified ? "unidentified" : undefined

  const renderShell = (children: React.ReactNode): React.JSX.Element => {
    const className = clsx(classes.card, variant === "compact" && classes.compact)
    if (interactive) {
      return (
        <button
          type="button"
          className={className}
          data-state={dataState}
          data-interactive="true"
          data-testid={testId}
          onClick={() => onEdit?.(editKey)}
          aria-label={editLabel}
        >
          {children}
        </button>
      )
    }
    return (
      <div className={className} data-state={dataState} data-testid={testId}>
        {children}
      </div>
    )
  }

  // Compact — a single-line pill: avatar + name + dimmed source glyph, no subline.
  if (variant === "compact") {
    return renderShell(
      <>
        <CardAvatar speaker={speaker} />
        <span className={classes.compactName}>
          <SpeakerName speaker={speaker} />
        </span>
        <span className={classes.cardSourceGlyph}>
          <RecognitionIcon source={speaker.recognitionSource} />
        </span>
      </>,
    )
  }

  // ── Card subline — priority: fresh → pending → suggestion → AI-named →
  //    unidentified (listen) → named (source glyph + title / talk share). ──────
  const renderSubline = (): React.JSX.Element | null => {
    if (fresh) {
      return (
        <span className={classes.cardSub}>
          <IconUserCheck size={12} className={classes.recognitionIcon} aria-hidden />
          <Trans>voice saved</Trans>
        </span>
      )
    }
    if (isPending) {
      return (
        <span className={classes.cardSub}>
          <span className={classes.spinner} aria-hidden />
          <Trans>saving…</Trans>
        </span>
      )
    }
    if (isSuggested && suggestion) {
      const score = Math.round(suggestion.score * 100)
      const evidence = summariseSpeakerEvidence(suggestion.sources, i18n)
      const detail = evidence ? `${evidence} ${score}%` : `${score}%`
      return (
        <span className={`${classes.cardSub} ${classes.cardSubOneLine}`} title={detail}>
          <span className={classes.cardSubText}>{`${detail} ·`}</span>
          <ActionChip
            tone="yes"
            ariaLabel={i18n._(msg`Confirm ${suggestion.name}`)}
            onActivate={() => beginConfirm(suggestion.name)}
          >
            <Trans>Yes</Trans>
          </ActionChip>
          <ActionChip
            tone="no"
            ariaLabel={i18n._(msg`Reject ${suggestion.name}`)}
            onActivate={() => onRejectSuggestion?.(editKey, suggestion.name)}
          >
            <Trans>No</Trans>
          </ActionChip>
        </span>
      )
    }
    if (isAiNamed) {
      return (
        <span className={classes.cardSub}>
          <Trans>AI-named</Trans>
          {" · "}
          <ActionChip tone="yes" ariaLabel={i18n._(msg`Confirm ${name}`)} onActivate={() => beginConfirm(name)}>
            <Trans>confirm</Trans>
          </ActionChip>
        </span>
      )
    }
    if (isUnidentified) {
      const firstSpoke = speaker.firstSpokeSeconds != null ? fmtTimestamp(speaker.firstSpokeSeconds) : null
      return (
        <span className={classes.cardSub}>
          {onListen ? (
            <ActionChip tone="listen" ariaLabel={i18n._(msg`Listen to ${name}`)} onActivate={() => onListen(editKey)}>
              <IconPlayerPlayFilled size={8} />
            </ActionChip>
          ) : null}
          <Trans>listen</Trans>
          {firstSpoke != null ? (
            <>
              {" · "}
              <Trans>first spoke {firstSpoke}</Trans>
            </>
          ) : null}
        </span>
      )
    }
    // Named / confirmed — source glyph then title, else talk share (never both).
    const talkPercent = speaker.talkPercent
    const stat = speaker.title ?? (talkPercent != null ? i18n._(msg`${talkPercent}% of talk`) : null)
    if (!speaker.recognitionSource && stat == null) return null
    return (
      <span className={classes.cardSub}>
        <RecognitionIcon source={speaker.recognitionSource} />
        {stat}
      </span>
    )
  }

  const showSparkles = speaker.recognitionSource === "llm" && speaker.status !== "confirmed" && !isSuggested

  return renderShell(
    <>
      <CardAvatar speaker={speaker} />
      <span className={classes.cardBody}>
        <span className={classes.cardName}>
          {isSuggested && suggestion ? `${suggestion.name}?` : <SpeakerName speaker={speaker} />}
          {showSparkles ? <IconSparkles size={12} className={classes.nameSparkle} aria-hidden /> : null}
        </span>
        {renderSubline()}
      </span>
      {interactive ? <IconPencil size={11} className={classes.cardPencil} aria-hidden /> : null}
      {fresh ? (
        <span className={classes.savedTag}>
          <Trans>saved</Trans>
        </span>
      ) : null}
    </>,
  )
}

/**
 * The one presentational speaker chip. Given a resolved {@link ISpeakerVM} it
 * renders the profile `card` / `compact` pill, the legend dot + name, the roster
 * button, an inline coloured name, or an identity avatar — and, when the speaker
 * is a pending suggestion, the confirm / reject affordance. Dumb: props in,
 * events out, no data hooks.
 */
export function SpeakerChip({
  speaker,
  variant,
  size = "sm",
  onEdit,
  onConfirmSuggestion,
  onRejectSuggestion,
  onListen,
  suggestionPending,
  readOnlySuggestion = false,
  "data-testid": dataTestid,
}: IProps): React.JSX.Element {
  const name = speaker.displayName
  const editKey = speaker.label ?? speaker.displayName
  const testId = dataTestid ?? `speaker-chip-${editKey}`

  // Profile-card variants own their layout + interactions (and hooks) — delegate.
  if (variant === "card" || variant === "compact") {
    return (
      <SpeakerProfileCard
        speaker={speaker}
        variant={variant}
        size={size}
        onEdit={onEdit}
        onConfirmSuggestion={onConfirmSuggestion}
        onRejectSuggestion={onRejectSuggestion}
        onListen={onListen}
        suggestionPending={suggestionPending}
        data-testid={dataTestid}
      />
    )
  }

  // Avatar is purely graphical — identity fill + initials, no name, no suggestion.
  if (variant === "avatar") {
    return (
      <span
        className={classes.avatar}
        data-size={size}
        style={{ background: speaker.resolvedColor, color: identityInkColor(speaker.resolvedColor) }}
        title={speaker.title ? `${name} · ${speaker.title}` : name}
        role="img"
        aria-label={name}
        data-testid={testId}
      >
        {speaker.initials}
      </span>
    )
  }

  // `inline` — the last non-card, non-avatar variant (legend/roster were
  // replaced by the card/compact profile treatment across every host).
  const chip = (
    <span
      className={classes.inline}
      data-size={size}
      style={{ color: identityTextColor(speaker.resolvedColor) }}
      data-testid={testId}
    >
      <SpeakerName speaker={speaker} />
    </span>
  )

  if (speaker.status === "suggested" && speaker.suggestion && (onConfirmSuggestion || readOnlySuggestion)) {
    return (
      <span className={classes.suggestionWrap}>
        {chip}
        <SuggestionAffordance
          suggestion={speaker.suggestion}
          editKey={editKey}
          resolvedColor={speaker.resolvedColor}
          pending={suggestionPending}
          readOnly={readOnlySuggestion}
          onConfirm={onConfirmSuggestion}
          onReject={onRejectSuggestion}
        />
      </span>
    )
  }

  return chip
}
